<template>
  <div
    v-if="location.questions_error"
    class="error_state"
  >
    <p>We are unable to fetch or create questions for this location.</p>
    <p>{{ errorState }}</p>
  </div>
  <div v-else>
    <div
      v-if="canEditLocation"
      class="white_box"
    >
      <h3>Add a question</h3>
      <textarea
        v-model="questionText"
        :class="[
          'question_text',
          questionText ? 'expand' : '',
        ]"
        placeholder="Start typing here to add a question for your business"
      />
      <p class="info-text">
        Spam questions may be removed by Google
        <i
          class="fal fa-question-circle tooltipster"
          title="
            Google will remove any questions that it deems as spam, this can be questions that include links,
            emails or phone numbers. If you post a question and it disappears from the platform within 24 hours
            it will likely be due to it being flagged as spam. Unfortunately Google does not inform us if it deems
            a questions as spam at the point of posting.
          "
        />
      </p>
      <div
        v-if="questionsValidationErrors"
        class="validation_errors"
      >
        <small
          v-for="(error, index) in questionsValidationErrors['questionText']"
          :key="index"
          class="validation"
        >
          {{ error }}
        </small>
      </div>
      <div
        v-if="unacceptedContent"
        class="validation_errors"
      >
        <small class="validation">
          Please note: Questions that contain urls, phone numbers or email addresses are often removed by google
        </small>
      </div>
      <primary-button
        v-if="questionText"
        :class="[
          'add_question',
          questionsLoading ? 'disabled' : ''
        ]"
        size="md"
        @click="create"
      >
        {{ questionsLoading ? 'Adding' : 'Add' }} Question
        <i
          v-if="questionsLoading"
          class="fa fa-spinner fa-spin"
        />
      </primary-button>
    </div>
    <question-and-answer
      v-for="(question, questionIndex) in questions"
      :key="questionIndex"
      :question="question"
    />
    <div
      v-if="questions === null"
      class="loading_questions"
    >
      Loading
    </div>
    <div
      v-if="questions && questions.length === 0"
      class="no_questions"
    >
      <p>
        We haven't currently found any questions for this location.
        You can use the field above to start adding your own.
      </p>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "../../../global/buttons/PrimaryButton";
import QuestionAndAnswer from "../items/QuestionAndAnswer";
import {mapActions, mapGetters} from "vuex";
import {checkForUnacceptedContent} from "../../../../modules/gbp/questions";

export default {
  components: {
    QuestionAndAnswer,
    PrimaryButton
  },
  data() {
    return {
      questionText: '',
      potentialUnacceptedContent: false,
    }
  },
  computed: {
    ...mapGetters('user', [
        'canEditLocation',
    ]),
    ...mapGetters('businessProfile', [
      'location',
      'selectedLocation',
      'questions',
      'questionsValidationErrors',
      'questionsLoading',
    ]),
    errorState() {
      if (this.location.questions_error === 'UNVERIFIED_LOCATION') {
        return 'Your Google Business Profile location needs verifying.';
      }
      if (this.location.questions_error === 'INELIGIBLE_PLACE') {
        return 'This location is not eligible for Questions and Answers.';
      }
      return '';
    },
    unacceptedContent() {
      return checkForUnacceptedContent(this.questionText)
    }
  },
  methods: {
    ...mapActions('businessProfile', [
      'createQuestion',
    ]),
    create() {
      const {selectedLocation, questionText} = this;
      this.createQuestion({selectedLocation, questionText}).then(() => {
        this.questionText = '';
      });
    },
  },
}
</script>

<style lang="scss" scoped>
.white_box {
  margin-bottom: 1rem;

  > h3 {
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.042em;
    color: #040707;
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  > .add_question {
    margin-left: auto;
    width: 205px;
  }

  > .question_text {
    height: 3rem;

    &.expand {
      height: 4.3rem;
    }
  }

  :deep(.answer_text) {
    height: 3rem;

    &.expand {
      height: 4.3rem
    }
  }

  > .question_text, :deep(.answer_text) {
    width: 100%;
    color: #4E4E54;
    background-color: #fff;
    border: 1px solid #E8E5E2;
    padding: 13px 24.5px 11px;
    border-radius: 12px;
    font-size: 14px;
    letter-spacing: -0.5px;
    -webkit-transition: .3s;
    -o-transition: .3s;
    transition: .3s;

    &::-webkit-input-placeholder {
      color: #9999a3;
      -webkit-transition: .3s;
      transition: .3s;
    }

    &::-moz-placeholder {
      color: #9999a3;
      -moz-transition: .3s;
      transition: .3s;
    }

    &:-ms-input-placeholder {
      color: #9999a3;
      -ms-transition: .3s;
      transition: .3s;
    }

    &::-ms-input-placeholder {
      color: #9999a3;
      -ms-transition: .3s;
      transition: .3s;
    }

    &::placeholder {
      color: #9999a3;
      -webkit-transition: .3s;
      -o-transition: .3s;
      transition: .3s;
    }

    &:focus::-webkit-input-placeholder {
      color: transparent;
    }

    &:focus::-moz-placeholder {
      color: transparent;
    }

    &:focus:-ms-input-placeholder {
      color: transparent;
    }

    &:focus::-ms-input-placeholder {
      color: transparent;
    }

    &:focus::placeholder {
      color: transparent;
    }
  }

  .validation_errors {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .validation {
      text-align: left;
      color: red;
      margin-left: 10px;
    }
  }
}

.no_questions {
  padding: 0.5rem;
  border: 1px solid var(--primary-color);
  border-radius: 0.75rem;
  text-align: center;
  background: var(--white);

  p {
    color: var(--primary-color);
    margin-bottom: .25rem;
  }
}

.info-text {
  font-size: 11px;
  margin: 0;
}
</style>
